import { Controller } from "@hotwired/stimulus";
import { parseWallClock, shiftDays, weekdayBefore, formatWallClock } from "../utils/wallClock";

// Prefills the Event Start picker when a template is chosen, then previews the
// dates that start would produce.
//
// Each template carries its own suggestion because the date has to clear that
// template's registration lead time, otherwise the new event opens registration
// in the past.
//
// The preview resolves offsets client-side purely to render a sentence.
// Event::ApplyTemplateDates remains the source of truth on submit, so a drift
// between the two is cosmetic. GR-4097: the arithmetic is on wall-clock values
// (utils/wallClock), so the preview is the same whatever the browser's zone,
// and "already passed" compares against the template's own `now`, sent by the
// server in the template's zone.
export default class extends Controller {
  static targets = ["input", "template", "preview", "previewText"];
  static values = { suggestions: Object, schedules: Object };

  // After a 422 re-render the template is already chosen, so preview it
  // rather than waiting for a change that never comes.
  connect() {
    if (this.hasTemplateTarget && this.templateTarget.value) {
      this.selectedTemplateId = this.templateTarget.value;
      this.preview();
    }
  }

  suggest(event) {
    const suggested = this.suggestionsValue[event.target.value];
    if (suggested) {
      this.inputTarget.value = suggested;
    }

    this.selectedTemplateId = event.target.value;
    this.preview();
  }

  preview() {
    const schedule = this.schedulesValue[this.selectedTemplateId];
    const start = parseWallClock(this.inputTarget.value);

    if (!schedule || !start) {
      this.previewTarget.hidden = true;
      return;
    }

    const opens = shiftDays(start, -schedule.opens_offset_days);
    const closes = schedule.closes_rule
      ? weekdayBefore(start, schedule.closes_rule)
      : shiftDays(start, -schedule.closes_offset_days);
    const ends = new Date(start.getTime() + schedule.duration_seconds * 1000);

    // GR-4097: the server asks for a close date in these cases; say so up
    // front. A close still ahead but before registration opens has not passed.
    const now = parseWallClock(schedule.now);
    let passed = "";
    if (now && closes < now) {
      passed = " That has already passed, so you'll be asked for a registration close date.";
    } else if (closes < opens) {
      passed = " That is before registration opens, so you'll be asked for a registration close date.";
    }

    this.previewTextTarget.textContent =
      `Registration opens ${formatWallClock(opens)} and closes ${formatWallClock(closes)}.${passed} ` +
      `The event runs until ${formatWallClock(ends)}.`;
    this.previewTarget.hidden = false;
  }
}
